import { useMemo } from "react";
import {
  Box,
  CssBaseline,
  Stack,
  ThemeProvider,
  createTheme,
} from "@mui/material";
import { useParams } from "react-router-dom";
import { useNavBarMode, useNavBarTitle } from "components/NavBar";
import { type DatasetKind } from "../api";
import t from "../translations";
import { DatasetDetailProvider, useDatasetDetail } from "./DatasetDetailContext";
import DatasetImageList from "./DatasetImageList";
import DatasetImagePanel from "./DatasetImagePanel";
import CroppedImages from "./CroppedImages";
import AllCropsView from "./AllCropsView";
import DraggablePanels, {
  type DraggablePanel,
} from "./DraggablePanels/DraggablePanels";

interface DatasetDetailProps {
  kind?: DatasetKind;
}

export default function DatasetDetail({
  kind = "object",
}: DatasetDetailProps = {}) {
  const params = useParams<{ datasetId: string; imageId: string }>();
  const datasetId = params.datasetId ? parseInt(params.datasetId) : 0;
  const imageId = params.imageId ? parseInt(params.imageId) : 0;

  const darkTheme = useMemo(
    () =>
      createTheme({
        palette: {
          mode: "dark",
          background: { default: "#1e1e1e", paper: "#2d2d2d" },
        },
      }),
    []
  );

  useNavBarMode("dark");

  return (
    <ThemeProvider theme={darkTheme}>
      <CssBaseline enableColorScheme />
      <Box
        sx={{
          height: "100%",
          width: "100%",
          bgcolor: "background.default",
          color: "text.primary",
        }}
      >
        <DatasetDetailProvider
          datasetId={datasetId}
          imageId={imageId}
          kind={kind}
        >
          <DatasetDetailContent imageSelected={Boolean(params.imageId)} />
        </DatasetDetailProvider>
      </Box>
    </ThemeProvider>
  );
}

function DatasetDetailContent({ imageSelected }: { imageSelected: boolean }) {
  const { datasetName, viewMode } = useDatasetDetail();

  useNavBarTitle(datasetName ? `Dataset: ${datasetName}` : t.PageTitle);

  const cropsMode = viewMode === "crops";

  const panels = useMemo<DraggablePanel[]>(
    () => [
      {
        key: "imageList",
        show: true,
        defaultWidth: 320,
        content: <DatasetImageList />,
      },
      {
        // In crops mode, AllCropsView grows only when no image is selected;
        // when one is, it falls back to a fixed width so the image panel
        // takes the remainder and the 3-panel row still fits on ~1024px.
        key: "allCrops",
        show: cropsMode,
        grow: cropsMode && !imageSelected,
        defaultWidth: 400,
        content: <AllCropsView />,
      },
      {
        // Per-image crops thumbnail strip — only useful in image mode; the
        // all-crops grid already renders the selected image's crops.
        key: "croppedImages",
        show: !cropsMode && imageSelected,
        defaultWidth: 246,
        content: <CroppedImages />,
      },
      {
        key: "imagePanel",
        show: imageSelected,
        grow: true,
        content: <DatasetImagePanel />,
      },
    ],
    [cropsMode, imageSelected],
  );

  return (
    <Stack
      sx={{
        flexDirection: "row",
        height: "100%",
        width: "100%",
        overflow: "hidden",
      }}
    >
      <DraggablePanels panels={panels} storageId="datasetDetail" />
    </Stack>
  );
}
